import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Message, Radio, Dropdown, Segment, Header } from 'semantic-ui-react'
import { AppPrimaryButton } from 'elements'
import { LoaderBar } from 'components'
import { useAuth } from 'context/user'
import { useNotification } from 'hooks/toast'
import { HomeDashboardService } from 'utility/services/home-dashboard'
import { HOME_DASHBOARD_PATH, refreshHomeDashboardStatus } from 'utility/home-dashboard'

// Settings -> Home dashboard (company admins). Backend: GET/POST user/Dashboard/Setting,
// GET/POST user/Dashboard/FindingAnswers. "classic" keeps today's task dashboard.

const TYPE_INFO = {
    auto: ['Auto-detect (recommended)', 'Re-evaluated from your users, customers, templates, audits, tickets, schedules, digests and invoices.'],
    solo: ['Solo inspector / consultant', 'One person doing the audits.'],
    ops: ['Operations team, one site', 'e.g. a hotel or plant running daily checklists.'],
    field: ['Multi-customer field service', 'e.g. a field manager visiting many customer sites.'],
    records: ['Production records at volume', 'e.g. thousands of records a month, workflows, digests.'],
    plans: ['Action-plan driven audits', 'Few audits, many corrective-action tickets.'],
    classic: ['Classic (current layout)', 'Keep the existing task dashboard. The new home page is not used.'],
}

// [key, label, when Auto shows it] - the rules are HomeDashboardProfileService.DetectCapabilities on the backend
const BLOCKS = [
    ['Team', 'Team blocks (activity, team table)', 'Auto shows it when the company has more than 1 active user.'],
    ['Sites', 'Site / template health', 'Auto groups by customer when there is more than 1 customer, else by template when 3 or more templates were used in the last 90 days.'],
    ['Schedule', 'Planned vs completed', 'Auto shows it when there is an active schedule or a task created in the last 90 days.'],
    ['Findings', 'Findings', 'Auto always shows it.'],
    ['Tickets', 'Tickets and escalations', 'Auto shows it when there is a ticket or an auto-ticket rule.'],
    ['Workflow', 'Workflow phases', 'Auto shows it when a template has a section workflow.'],
    ['Digests', 'Digest status', 'Auto shows it when a digest email is set up.'],
    ['Invoices', 'Invoices', 'Auto shows it when there is an invoice.'],
    ['Scoring', 'Scores', 'Auto shows it when an audit completed in the last 90 days has a calculated score.'],
    ['HighVolume', 'Volume counters', 'Auto shows it at 600 or more audits in the last 90 days.'],
    ['Photos', 'Photo evidence', 'Auto shows it when an audit of the last 90 days has a photo.'],
    ['Assets', 'Assets', 'Auto shows it when the company has an asset.'],
]

const BOOL_OPTIONS = [
    { key: 'auto', value: 'auto', text: 'Auto' },
    { key: 'on', value: 'on', text: 'Show' },
    { key: 'off', value: 'off', text: 'Hide' },
]
const SITES_OPTIONS = [
    { key: 'auto', value: 'auto', text: 'Auto' },
    { key: 'customer', value: 'customer', text: 'By customer' },
    { key: 'template', value: 'template', text: 'By template' },
    { key: 'none', value: 'none', text: 'Hide' },
]
const UNIT_OPTIONS = [
    { key: 'auto', value: 'auto', text: 'From the dashboard type' },
    { key: 'audits', value: 'audits', text: 'audits' },
    { key: 'inspections', value: 'inspections', text: 'inspections' },
    { key: 'checklists', value: 'checklists', text: 'checklists' },
    { key: 'records', value: 'records', text: 'records' },
]

const toChoice = (key, v) => {
    if (key === 'Sites') return v ? String(v) : 'auto'
    if (v === true) return 'on'
    if (v === false) return 'off'
    return 'auto'
}
const fromChoice = (key, c) => {
    if (c === 'auto') return null
    if (key === 'Sites') return c
    return c === 'on'
}

const errorText = err => {
    const e = err?.error
    return (
        e?.Message ||
        e?.message ||
        (typeof e === 'string' ? e : '') ||
        'The settings could not be saved. Please try again.'
    )
}

// What the block does right now: the admin's choice wins over auto-detect. Before, the line always said
// "detected: shown / hidden" (= what Auto would do), even under a block set to Hide - testers read it as a bug.
const autoWord = (key, detected) => {
    const v = detected?.[key]
    if (key === 'Sites') return v ? `by ${v}` : 'hidden'
    return v ? 'shown' : 'hidden'
}
const choiceWord = (key, choice) => {
    if (key === 'Sites') return choice === 'none' ? 'hidden' : `by ${choice}`
    return choice === 'on' ? 'shown' : 'hidden'
}
const blockStatus = (key, choice, detected) => {
    const auto = autoWord(key, detected)
    if (!choice || choice === 'auto') return `Auto: ${auto}`
    const mine = choiceWord(key, choice)
    return mine === auto ? `${mine[0].toUpperCase()}${mine.slice(1)} (your choice, same as Auto)` : `${mine[0].toUpperCase()}${mine.slice(1)} (your choice; Auto would be ${auto})`
}

export const HomeDashboardSettings = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { successToast, errorToast } = useNotification()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [savingAnswers, setSavingAnswers] = useState(false)
    const [forbidden, setForbidden] = useState(false)
    const [error, setError] = useState('')
    const [info, setInfo] = useState(null)
    const [type, setType] = useState('auto')
    const [unitWord, setUnitWord] = useState('auto')
    const [ageDays, setAgeDays] = useState(7)
    const [blocks, setBlocks] = useState({})
    const [answers, setAnswers] = useState(null)
    const [answersText, setAnswersText] = useState('')

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const [s, a] = await Promise.all([
                // cached counts (10 min) are enough here; refresh=true would recompute the company profile
                HomeDashboardService.getSetting(),
                HomeDashboardService.getFindingAnswers().catch(() => null),
            ])
            setInfo(s)
            // No saved row = the backend default layout (classic until HomeDashboard:DefaultLayout = home).
            // Show what the company really has, so saving another field never switches the new home on.
            const noRow = s?.Setting?.IsDefault === true
            const effectiveClassic = String(s?.Profile?.Type || '').toLowerCase() === 'classic'
            setType(noRow && effectiveClassic ? 'classic' : s?.Setting?.DashboardType || 'auto')
            setUnitWord(s?.Setting?.UnitWord || 'auto')
            setAgeDays(s?.Setting?.FindingAgeDays || 7)
            const b = {}
            BLOCKS.forEach(([k]) => {
                b[k] = toChoice(k, s?.Setting?.Blocks?.[k])
            })
            setBlocks(b)
            setAnswers(a)
            setAnswersText(((a && a.Company) || []).join('\n'))
        } catch (err) {
            if (err?.status === 403) setForbidden(true)
            else setError('The dashboard settings could not be loaded.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const types = useMemo(() => {
        const list = (info?.Types || []).map(t => String(t).toLowerCase())
        const all = ['auto', ...list.filter(t => t !== 'auto')]
        if (!all.includes('classic')) all.push('classic')
        return all
    }, [info])

    const canEdit = info?.CanEdit === true
    const isClassic = type === 'classic'

    const save = async () => {
        const days = Number(ageDays)
        if (!Number.isFinite(days) || days < 1 || days > 365) {
            errorToast('Finding age must be between 1 and 365 days.')
            return
        }
        setSaving(true)
        setError('')
        try {
            const Blocks = {}
            BLOCKS.forEach(([k]) => {
                Blocks[k] = fromChoice(k, blocks[k] || 'auto')
            })
            await HomeDashboardService.saveSetting({
                DashboardType: type,
                UnitWord: unitWord === 'auto' ? null : unitWord,
                FindingAgeDays: days,
                Blocks,
            })
            await refreshHomeDashboardStatus(user)
            successToast(isClassic ? 'Saved. The classic task dashboard stays your home page.' : 'Saved. The new home dashboard is on.')
            await load()
        } catch (err) {
            const message = errorText(err)
            setError(message)
            errorToast(message)
        } finally {
            setSaving(false)
        }
    }

    const saveAnswers = async () => {
        const list = answersText
            .split(/\r?\n|,/)
            .map(x => x.trim())
            .filter(Boolean)
        setSavingAnswers(true)
        try {
            await HomeDashboardService.saveFindingAnswers(list)
            successToast('Finding answers saved')
            const a = await HomeDashboardService.getFindingAnswers()
            setAnswers(a)
            setAnswersText(((a && a.Company) || []).join('\n'))
        } catch (err) {
            errorToast(errorText(err))
        } finally {
            setSavingAnswers(false)
        }
    }

    if (loading) return <LoaderBar content="Loading dashboard settings…" />
    if (forbidden) return <Message warning>The new home dashboard is not available for your company.</Message>
    if (!info) return <Message negative>{error || 'The dashboard settings could not be loaded.'}</Message>

    const profile = info.Profile || {}
    const detectedName = profile.DetectedType ? TYPE_INFO[profile.DetectedType]?.[0] || profile.DetectedType : ''
    return (
        <Segment raised>
            <Header as="h3">Home dashboard</Header>
            <p style={{ color: '#667085', marginTop: -6 }}>
                The page users see after they sign in. Admins get the leadership view, everyone else the auditor view.
                {profile.TypeName ? ` Auto-detect currently picks: ${profile.DetectedType ? TYPE_INFO[profile.DetectedType]?.[0] || profile.DetectedType : profile.TypeName}.` : ''}
            </p>
            {!canEdit && <Message info>Only a company admin can change these settings.</Message>}
            {error && <Message negative>{error}</Message>}

            <Form>
                <Form.Field>
                    <label>Dashboard type</label>
                    {types.map(t => (
                        <div key={t} style={{ margin: '8px 0' }}>
                            <Radio
                                label={
                                    t === 'auto' && detectedName
                                        ? `${TYPE_INFO.auto[0]} - currently ${detectedName}`
                                        : TYPE_INFO[t]?.[0] || t
                                }
                                name="dashboardType"
                                value={t}
                                checked={type === t}
                                disabled={!canEdit}
                                onChange={() => setType(t)}
                            />
                            {TYPE_INFO[t]?.[1] && (
                                <div style={{ color: '#667085', fontSize: 12.5, marginLeft: 26 }}>{TYPE_INFO[t][1]}</div>
                            )}
                        </div>
                    ))}
                </Form.Field>

                {!isClassic && (
                    <>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Word used on the page</label>
                                <Dropdown
                                    selection
                                    options={UNIT_OPTIONS}
                                    value={unitWord}
                                    disabled={!canEdit}
                                    onChange={(e, { value }) => setUnitWord(value)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>A finding with no ticket is past due after (days)</label>
                                <Form.Input
                                    type="number"
                                    min={1}
                                    max={365}
                                    value={ageDays}
                                    disabled={!canEdit}
                                    onChange={(e, { value }) => setAgeDays(value)}
                                />
                            </Form.Field>
                        </Form.Group>

                        <Form.Field>
                            <label>Blocks</label>
                            <div style={{ color: '#667085', fontSize: 12.5, marginBottom: 8 }}>
                                Auto follows what auto-detect decided from your company's data (the rule is under each block). Show or Hide
                                always wins over Auto. A block that is on but has no data shows an empty state.
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                                {BLOCKS.map(([k, label, rule]) => (
                                    <div key={k}>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
                                        <Dropdown
                                            selection
                                            fluid
                                            options={k === 'Sites' ? SITES_OPTIONS : BOOL_OPTIONS}
                                            value={blocks[k] || 'auto'}
                                            disabled={!canEdit}
                                            onChange={(e, { value }) => setBlocks(prev => ({ ...prev, [k]: value }))}
                                        />
                                        <div style={{ color: '#344054', fontSize: 12 }}>{blockStatus(k, blocks[k], info.Detected)}</div>
                                        <div style={{ color: '#98a2b3', fontSize: 11.5 }}>{rule}</div>
                                    </div>
                                ))}
                            </div>
                        </Form.Field>
                    </>
                )}

                {canEdit && (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <AppPrimaryButton onClick={save} loading={saving} disabled={saving}>
                            Save
                        </AppPrimaryButton>
                        {info?.Profile?.Type && info.Profile.Type !== 'classic' && (
                            <button
                                type="button"
                                onClick={() => navigate(HOME_DASHBOARD_PATH)}
                                style={{ background: 'none', border: 0, color: '#1e44b8', fontWeight: 600, cursor: 'pointer' }}
                            >
                                Open the home dashboard
                            </button>
                        )}
                    </div>
                )}
            </Form>

            {!isClassic && answers && (
                <>
                    <Header as="h4" style={{ marginTop: 28 }}>
                        What counts as a finding
                    </Header>
                    <p style={{ color: '#667085', fontSize: 13 }}>
                        A selected answer whose text is in this list. Built-in: {(answers.Global || []).join(', ') || '–'}.
                        {answers.TriggerValues?.length
                            ? ` Auto-ticket trigger answers also count on their own question: ${answers.TriggerValues.join(', ')}.`
                            : ''}
                    </p>
                    <Form>
                        <Form.TextArea
                            label="Your company's own answers (one per line)"
                            rows={4}
                            value={answersText}
                            disabled={!canEdit}
                            onChange={(e, { value }) => setAnswersText(value)}
                            placeholder={'e.g.\nNot acceptable\nNeeds attention'}
                        />
                        {canEdit && (
                            <AppPrimaryButton onClick={saveAnswers} loading={savingAnswers} disabled={savingAnswers}>
                                Save finding answers
                            </AppPrimaryButton>
                        )}
                    </Form>
                </>
            )}
        </Segment>
    )
}
